import React, { useState } from 'react'

// ── Accordion section ─────────────────────────────────────────────────────────

function HowSection({ iconStyle, icon, title, sub, children, defaultOpen = true }) {
    const [open, setOpen] = useState(defaultOpen)
    return (
        <div className="sm-how-section">
            <div className="sm-how-section-header" onClick={() => setOpen((o) => !o)}>
                <div className="sm-how-section-icon" style={iconStyle}>
                    <i className={`ti ${icon}`} />
                </div>
                <div className="sm-how-section-titles">
                    <div className="sm-how-section-title">{title}</div>
                    <div className="sm-how-section-sub">{sub}</div>
                </div>
                <i className={`ti ti-chevron-down sm-how-section-chevron${open ? ' open' : ''}`} />
            </div>
            {open && (
                <div className="sm-how-section-body">
                    {children}
                </div>
            )}
        </div>
    )
}

// ── Bullet list ───────────────────────────────────────────────────────────────

function HowList({ items }) {
    return (
        <div className="sm-how-list">
            {items.map((item, i) => (
                <div key={i} className="sm-how-list-item">
                    <i className="ti ti-point" />
                    <span>{item}</span>
                </div>
            ))}
        </div>
    )
}

// ── Tip callouts ──────────────────────────────────────────────────────────────

function Tip({ children }) {
    return (
        <div className="sm-how-tip">
            <i className="ti ti-bulb" />
            <span>{children}</span>
        </div>
    )
}

function WarnTip({ children }) {
    return (
        <div className="sm-how-warn-tip">
            <i className="ti ti-alert-triangle" />
            <span>{children}</span>
        </div>
    )
}

// ── Sub-heading (used inside SWMS assignment section) ─────────────────────────

function SubHead({ children }) {
    return <div className="sm-how-sub-head">{children}</div>
}

// ── Main component ────────────────────────────────────────────────────────────

export default function SwmsHowItWorks() {
    return (
        <div className="sm-how-page">
            <div className="sm-how-inner">
                {/* 4-step flow */}
                <div className="sm-how-flow">
                    {[
                        { num: 1, icon: '📄', title: 'Create a draft',  desc: 'Start from a template or from scratch. Fill in tasks, hazards and controls.' },
                        { num: 2, icon: '✅', title: 'Publish',          desc: 'Lock the document. It becomes a permanent compliance record.' },
                        { num: 3, icon: '🏗️', title: 'Assign to site',  desc: 'Link the published SWMS to a site and select which trades it applies to.' },
                        { num: 4, icon: '✍️', title: 'Workers sign',     desc: 'Workers scan a QR code on-site, read the SWMS and sign digitally.' },
                    ].map((step) => (
                        <div key={step.num} className="sm-how-flow-step">
                            <div className="sm-how-step-num">{step.num}</div>
                            <div className="sm-how-step-icon">{step.icon}</div>
                            <div className="sm-how-step-title">{step.title}</div>
                            <div className="sm-how-step-desc">{step.desc}</div>
                        </div>
                    ))}
                </div>

                {/* Accordion sections */}
                <div className="sm-how-sections">

                    {/* What is a SWMS? */}
                    <HowSection
                        iconStyle={{ background: '#DBEAFE', color: '#1D4ED8' }}
                        icon="ti-info-circle"
                        title="What is a SWMS?"
                        sub="Legal background and when one is required"
                    >
                        <p className="sm-how-para">
                            A <b>Safe Work Method Statement (SWMS)</b> is a document that describes how high risk
                            construction work will be carried out safely. It must identify each work task, the hazards
                            and risks involved, and the control measures that will be used to manage those risks.
                        </p>
                        <p className="sm-how-para">
                            Under the <b>Work Health and Safety Regulation 2017</b>, a SWMS must be prepared before
                            any high risk construction work begins, kept on‑site and accessible to all relevant workers,
                            and signed by each worker to confirm they have read and understood it.
                        </p>
                        <Tip>
                            <b>High risk construction work</b> includes working at heights over 2 m, work near
                            energised electrical installations, work involving pressurised lines, demolition, confined
                            spaces and more. The full list is shown in the document editor.
                        </Tip>
                    </HowSection>

                    {/* Templates */}
                    <HowSection
                        iconStyle={{ background: '#EFF6FF', color: '#1E40AF' }}
                        icon="ti-layout-grid"
                        title="Templates"
                        sub="Pre-built starting points for common trade activities"
                    >
                        <p className="sm-how-para">
                            Templates are pre-built SWMS structures provided for common types of construction work —
                            plumbing, electrical, roofing, carpentry and so on. They include a suggested set of tasks,
                            hazards and control measures relevant to that trade.
                        </p>
                        <HowList
                            items={[
                                <>Templates are <b>read-only</b> — you cannot modify them directly.</>,
                                <>Click <b>Use template</b> to create a new draft pre-filled with the template content. You then customise it for your specific job.</>,
                                <>Templates are never assigned to sites — only published documents are.</>,
                            ]}
                        />
                        <Tip>
                            Using a template saves time and reduces the chance of missing a common hazard. Always
                            review and adjust the content to reflect the actual conditions on your specific site.
                        </Tip>
                    </HowSection>

                    {/* Drafts */}
                    <HowSection
                        iconStyle={{ background: '#FFFBEB', color: '#B45309' }}
                        icon="ti-pencil"
                        title="Drafts"
                        sub="Works in progress — edit freely before committing"
                    >
                        <p className="sm-how-para">
                            A draft is a SWMS that is still being prepared. Drafts are flexible — you can edit,
                            copy, rename or delete them at any time. No work on‑site can proceed against a draft;
                            it must first be published.
                        </p>
                        <HowList
                            items={[
                                <><b>Edit</b> — open the full editor to update project details, tick applicable high risk categories, and fill in the tasks/hazards/controls table. Save your changes as a draft at any time.</>,
                                <><b>Copy</b> — duplicate the draft to create a variation (e.g. for a different site or scope).</>,
                                <><b>Publish</b> — when the draft is ready, use the Publish button on the draft card to lock it into a permanent compliance record. A confirmation is required before this happens.</>,
                                <><b>Delete</b> — remove drafts you no longer need. Deleted drafts are gone permanently.</>,
                            ]}
                        />
                        <WarnTip>
                            Once you publish a draft, <b>you cannot edit or delete it</b>. Make sure the content is
                            complete and accurate before publishing.
                        </WarnTip>
                    </HowSection>

                    {/* Published SWMS */}
                    <HowSection
                        iconStyle={{ background: '#F0FDF4', color: '#166534' }}
                        icon="ti-circle-check"
                        title="Published SWMS"
                        sub="Locked, permanent compliance records"
                    >
                        <p className="sm-how-para">
                            A published SWMS is an immutable legal document. Once published, it cannot be edited or
                            deleted. This immutability is deliberate — it protects the builder by ensuring the
                            compliance record cannot be altered after workers have signed it.
                        </p>
                        <HowList
                            items={[
                                <><b>View</b> — open the document in PDF format for review or printing.</>,
                                <><b>New draft</b> — if you need to make changes, create a new draft based on this published version. The original remains intact.</>,
                                <><b>Retire</b> — move a document that is no longer in use to the Retired section, where it becomes read-only. Existing assignments for that SWMS are not affected — they remain as independent records.</>,
                                <>Published SWMS can be <b>assigned to sites and trades</b> from the SWMS Assignment tab.</>,
                            ]}
                        />
                        <Tip>
                            It is good practice to include the version or date in the document name (e.g. &ldquo;Plumbing
                            Works &ndash; May 2026&rdquo;) so you can distinguish between revisions.
                        </Tip>
                    </HowSection>

                    {/* Retired SWMS */}
                    <HowSection
                        iconStyle={{ background: '#FAFAFA', color: '#52525B' }}
                        icon="ti-file-off"
                        title="Retired SWMS"
                        sub="Documents no longer in use, kept for compliance history"
                    >
                        <p className="sm-how-para">
                            Retired documents are SWMS that are no longer actively in use. They cannot be edited or
                            re-published, but they are retained permanently for your compliance history and can be
                            viewed at any time.
                        </p>
                        <HowList
                            items={[
                                <>Retiring a published SWMS makes it read-only. <b>Existing assignments for that SWMS are not affected</b> — each assignment is an independent record and remains exactly as it was, including all worker signatures collected to date.</>,
                                <>When a <b>site is marked as complete</b>, all signature and worker details for that site are preserved as part of the compliance record.</>,
                                <>Retired documents are visible to auditors and regulators as part of your compliance record.</>,
                                <>You cannot un-retire a document — if you need a current version, create a new draft from the published original.</>,
                            ]}
                        />
                    </HowSection>

                    {/* SWMS assignment */}
                    <HowSection
                        iconStyle={{ background: 'var(--sm-brand-50)', color: 'var(--sm-brand-600)' }}
                        icon="ti-git-branch"
                        title="SWMS assignment"
                        sub="Linking published SWMS to sites and trades"
                    >
                        <p className="sm-how-para">
                            Assigning a SWMS connects a published document to a specific site and one or more trades.
                            This is what makes it available for workers on that site to read and sign.
                        </p>
                        <HowList
                            items={[
                                <>One SWMS can be assigned to <b>multiple sites</b> — create a separate assignment for each site.</>,
                                <>You can scope an assignment to specific trades — for example, assign &ldquo;Roofing Works&rdquo; to Roofers only, even if the site has other trades.</>,
                                <><b>Active</b> assignments are open for signing. Workers on the assigned site and trade can access the SWMS via QR code.</>,
                                <><b>Archiving</b> an assignment closes it for that specific site and trade — no further signatures can be collected against it. The record of all signatures collected to date is preserved. Note that archiving an assignment does not affect the underlying SWMS document in the store.</>,
                            ]}
                        />

                        <SubHead>Filtering the assignment list</SubHead>
                        <p className="sm-how-para" style={{ marginTop: 0 }}>
                            The filter bar at the top of the Assignment tab lets you quickly narrow down what is shown
                            in the grid. All filters work together — only rows matching every active filter are displayed.
                        </p>
                        <div className="sm-how-list">
                            <div className="sm-how-list-item">
                                <i className="ti ti-search" style={{ color: 'var(--sm-text-tertiary)' }} />
                                <span><b>Text search</b> — type any part of a site address, SWMS name or trade into the search box. The grid instantly updates to show only rows that contain that text in any of those fields. Clear the box to show all results again.</span>
                            </div>
                            <div className="sm-how-list-item">
                                <i className="ti ti-circle-filled" style={{ color: '#1E40AF' }} />
                                <span><b>Active sites</b> — when toggled on, rows belonging to sites that are currently active are included. Active sites are shown with a green row highlight.</span>
                            </div>
                            <div className="sm-how-list-item">
                                <i className="ti ti-circle-check" style={{ color: '#166534' }} />
                                <span><b>Completed sites</b> — when toggled on, rows belonging to sites that have been marked complete are included. Completed site rows are shown with a grey highlight so you can tell them apart at a glance. Both toggles can be on at the same time to show everything.</span>
                            </div>
                            <div className="sm-how-list-item">
                                <i className="ti ti-tag" style={{ color: 'var(--sm-brand-600)' }} />
                                <span><b>Trade badges</b> — click one or more trade chips (Plumber, Electrician, Roofer, etc.) to restrict the grid to assignments for those trades only. Click a selected chip again to deselect it. When no trade chips are selected, all trades are shown.</span>
                            </div>
                            <div className="sm-how-list-item">
                                <i className="ti ti-x" style={{ color: 'var(--sm-rose-700)' }} />
                                <span><b>Clear filters</b> — resets the text search, deselects all trade chips, and returns the site status toggles to their default (Active sites only).</span>
                            </div>
                        </div>

                        <SubHead>SWMS view vs Site view</SubHead>
                        <p className="sm-how-para" style={{ marginTop: 0 }}>
                            The toggle in the top-right of the assignment grid switches between two ways of looking at
                            the same data.
                        </p>
                        <div className="sm-how-list">
                            <div className="sm-how-list-item">
                                <i className="ti ti-file-text" style={{ color: 'var(--sm-brand-600)' }} />
                                <span><b>SWMS view</b> — each row represents one assignment: the SWMS document, the trade it covers, and the sites it has been assigned to. Use this view when you want to see which documents are active, archive a specific assignment, or check signoff status per document. Site badges are colour-coded — green for active sites, grey for completed sites.</span>
                            </div>
                            <div className="sm-how-list-item">
                                <i className="ti ti-building" style={{ color: 'var(--sm-brand-600)' }} />
                                <span><b>Site view</b> — each row represents one site, showing its address, client, job number and status. Active site rows are highlighted green; completed site rows are grey. The Actions column provides quick access to view every SWMS document assigned to that site. Use this view when you want a site-by-site picture — for example, checking everything assigned to a particular job before a site inspection.</span>
                            </div>
                        </div>
                        <Tip>
                            Both views respect the same filters — switching between them after filtering keeps your
                            current search and status selection in place.
                        </Tip>
                    </HowSection>

                    {/* Worker signoff */}
                    <HowSection
                        iconStyle={{ background: '#FFF1F2', color: '#BE123C' }}
                        icon="ti-signature"
                        title="Worker signoff"
                        sub="How workers read and sign SWMS on-site"
                    >
                        <p className="sm-how-para">
                            Once a SWMS is assigned to a site, workers can access it by scanning a QR code displayed
                            at the site entry. Upon scanning, the worker is required to read the full SWMS and sign
                            digitally using their finger or stylus — they cannot proceed into the site without
                            completing this step.
                        </p>
                        <HowList
                            items={[
                                <>Each signature is recorded with the <b>worker&apos;s name, trade, date and time</b>.</>,
                                <>Workers can only see the SWMS documents for the <b>site they are currently checked into</b> — they cannot view or sign SWMS for other sites.</>,
                                <>The <b>View</b> button on any assignment shows the current signoff status — how many workers have signed, and each worker&apos;s signature with date and time.</>,
                                <>The full signoff record is included when you <b>Print / Save PDF</b> from the View screen — this is the document you provide to a regulator or principal contractor on request.</>,
                            ]}
                        />
                        <WarnTip>
                            Workers must sign <b>immediately after scanning the QR code</b> at site entry. They cannot
                            proceed on site without signing all active SWMS assigned to their trade. The WHS Regulation
                            requires signing as a precondition for commencing high risk construction work.
                        </WarnTip>
                    </HowSection>

                </div>{/* end sm-how-sections */}
            </div>{/* end sm-how-inner */}
        </div>
    )
}
